import { Badge, Flex, Spinner, Text, Tooltip } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { format, parseISO } from 'date-fns';

import {
  ActionIconButton,
  CopyOutline,
  DocumentDownloadOutline,
  EyeOutline,
} from '@aslanpay/portal-common-lib';

import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import {
  FileUploadRow,
  FileUploadStatus,
  FileUploadType,
  FILE_UPLOAD_STATUS_COLOR,
  FILE_UPLOAD_STATUS_LABELS,
  FILE_UPLOAD_TYPE_LABELS,
} from 'app/constants/fileUploads';

export interface FileUploadActions {
  download: (file: FileUploadRow) => void;
  viewDetails: (file: FileUploadRow) => void;
  copyId: (file: FileUploadRow) => void;
}

const uploadedByName = (file: FileUploadRow) => {
  const name = `${file.uploadedByFirstName ?? ''} ${file.uploadedByLastName ?? ''}`.trim();
  return name || '-';
};

export function getFileUploadColumns(actions: FileUploadActions, downloadingId?: string) {
  const columnHelper = createColumnHelper<FileUploadRow>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<FileUploadRow, any>[] = [
    columnHelper.accessor('name', {
      header: 'File name',
      enableSorting: false,
      cell: (info) => (
        <Text textStyle="smallMedium" color="brand.darkPurple" wordBreak="break-all">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('uploadType', {
      header: 'Type',
      enableSorting: false,
      cell: (info) => {
        const type = info.getValue() as FileUploadType;
        return FILE_UPLOAD_TYPE_LABELS[type] ?? type;
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: (info) => {
        const status = info.getValue() as FileUploadStatus;
        return (
          <Badge colorScheme={FILE_UPLOAD_STATUS_COLOR[status] ?? 'gray'} variant="subtle">
            {FILE_UPLOAD_STATUS_LABELS[status] ?? status}
          </Badge>
        );
      },
    }),
    columnHelper.display({
      id: 'uploadedBy',
      header: 'Uploaded by',
      cell: (info) => uploadedByName(info.row.original),
    }),
    columnHelper.accessor('createdAt', {
      header: 'Uploaded at',
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return value ? format(parseISO(value), 'dd/MM/yyyy HH:mm') : '-';
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const file = info.row.original;
        return (
          <Flex justify="center" gap="4px">
            <Tooltip label="Download" placement="top">
              <ActionIconButton
                aria-label="Download file"
                icon={
                  downloadingId === file.id ? (
                    <Spinner size="sm" />
                  ) : (
                    <DocumentDownloadOutline boxSize="18px" />
                  )
                }
                isDisabled={!!downloadingId}
                onClick={() => actions.download(file)}
              />
            </Tooltip>
            <Tooltip label="View details" placement="top">
              <ActionIconButton
                aria-label="View file details"
                icon={<EyeOutline boxSize="18px" />}
                onClick={() => actions.viewDetails(file)}
              />
            </Tooltip>
            <Tooltip label="Copy file ID" placement="top">
              <ActionIconButton
                aria-label="Copy file ID"
                icon={<CopyOutline boxSize="18px" />}
                onClick={() => actions.copyId(file)}
              />
            </Tooltip>
          </Flex>
        );
      },
    }),
  ];

  return columns;
}

export { TextWithClipboard };
